import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { colors, palette, radii, spacing } from '../../theme';

/** Current page: a stretched bar. Every other page: a plain dot. */
const SIZE = 6;
const ACTIVE_WIDTH = 24;
const DURATION = 280;

type PipProps = {
  active: boolean;
};

/**
 * One page marker, which grows from a dot into a bar when its page arrives.
 *
 * Width and colour are both animated, which rules out the native driver - it
 * only handles transform and opacity. That is fine here: three six-point views
 * are nothing to lay out, and doing it with a transform instead would scale
 * the rounded ends into ovals.
 */
function Pip({ active }: PipProps) {
  const progress = useRef(new Animated.Value(active ? 1 : 0)).current;

  useEffect(() => {
    const animation = Animated.timing(progress, {
      toValue: active ? 1 : 0,
      duration: DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    });
    animation.start();
    return () => animation.stop();
  }, [active, progress]);

  return (
    <Animated.View
      style={[
        styles.pip,
        {
          width: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [SIZE, ACTIVE_WIDTH],
          }),
          backgroundColor: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [colors.borderStrong, palette.green900],
          }),
        },
      ]}
    />
  );
}

type Props = {
  count: number;
  index: number;
  style?: StyleProp<ViewStyle>;
};

/**
 * Progress indicator, bottom-left. The page you are on is drawn as a bar and
 * the rest as dots, sitting tight together.
 *
 * The whole row is one accessibility node - a screen reader should hear
 * "page 2 of 3", not three anonymous decorations.
 */
function PageDashes({ count, index, style }: Props) {
  return (
    <View
      style={[styles.row, style]}
      accessible
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 1, max: count, now: index + 1 }}
      accessibilityLabel={`Page ${index + 1} of ${count}`}
    >
      {Array.from({ length: count }, (_, i) => (
        <Pip key={i} active={i === index} />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  pip: {
    height: SIZE,
    borderRadius: radii.pill,
  },
});

export default PageDashes;
